/**
 * ErrorBoundary - Catches React render errors and displays recovery UI
 * Prevents blank screen crashes from taking down the entire app
 */
import React from 'react';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Optional fallback UI. If not provided, a default recovery UI is shown */
  fallback?: React.ReactNode;
  /** Component name for error reporting */
  name?: string;
  /** If true, shows a minimal inline placeholder instead of full-screen error */
  inline?: boolean;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`[ErrorBoundary${this.props.name ? `: ${this.props.name}` : ''}] Caught error:`, error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Inline placeholder (for individual sections)
      if (this.props.inline) {
        return (
          <div className="p-4 rounded-xl bg-void-black/50 border border-red-500/30 text-center">
            <p className="text-red-400 text-sm font-mono mb-2">
              ⚠️ {this.props.name || 'Section'} failed to load
            </p>
            <button
              onClick={this.handleReset}
              className="px-3 py-1 text-xs rounded-lg bg-red-500/20 border border-red-500/30 text-red-300 hover:bg-red-500/30 transition-colors"
            >
              Retry
            </button>
          </div>
        );
      }

      // Full-screen recovery UI
      return (
        <div className="min-h-screen bg-black flex items-center justify-center p-4">
          <div className="max-w-lg w-full text-center space-y-6">
            <div className="text-6xl mb-4">💥</div>
            <h1 className="text-2xl font-orbitron text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-neon-magenta to-neon-purple">
              SYSTEM MALFUNCTION
            </h1>
            <p className="text-gray-400 font-rajdhani">
              THALIA caused a glitch in the matrix. Don't worry — your keys and funds are safe on-chain.
            </p>
            {this.state.error && (
              <details className="text-left bg-void-black/50 rounded-xl p-4 border border-red-500/20">
                <summary className="text-red-400 text-sm font-mono cursor-pointer">
                  Error Details
                </summary>
                <pre className="text-xs text-gray-500 mt-2 overflow-auto max-h-40 whitespace-pre-wrap">
                  {this.state.error.message}
                  {this.state.error.stack && `\n\n${this.state.error.stack}`}
                </pre>
              </details>
            )}
            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleReset}
                className="px-6 py-3 rounded-xl font-orbitron text-sm bg-gradient-to-r from-neon-cyan/20 to-neon-purple/20 border border-neon-cyan/30 text-neon-cyan hover:border-neon-cyan hover:shadow-[0_0_20px_rgba(0,255,255,0.3)] transition-all"
              >
                ◈ Try Again
              </button>
              <button
                onClick={this.handleReload}
                className="px-6 py-3 rounded-xl font-orbitron text-sm bg-gradient-to-r from-neon-magenta/20 to-neon-purple/20 border border-neon-magenta/30 text-neon-magenta hover:border-neon-magenta hover:shadow-[0_0_20px_rgba(255,0,128,0.3)] transition-all"
              >
                ⟳ Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Inline error boundary wrapper for individual lazy components
 * Use: <SafeSection name="Dashboard"><Dashboard /></SafeSection>
 */
export function SafeSection({ children, name }: { children: React.ReactNode; name: string }) {
  return (
    <ErrorBoundary name={name} inline>
      {children}
    </ErrorBoundary>
  );
}
